import { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../config';

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = () => {
        const token = localStorage.getItem('token');
        if (token) {
            fetch(`${API_URL}/api/sessions/logout`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                keepalive: true,
            }).catch(() => {});
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    useEffect(() => {
        const bootstrap = async () => {
            const token = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');

            if (!token || !storedUser) {
                setLoading(false);
                return;
            }

            try {
                JSON.parse(storedUser);
            } catch {
                logout();
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(`${API_URL}/api/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    localStorage.setItem('user', JSON.stringify(data.user));
                    setUser(data.user);
                } else {
                    logout();
                }
            } catch {
                // Offline or server down: trust cached user so POS can still load
                try {
                    setUser(JSON.parse(storedUser));
                } catch {
                    logout();
                }
            } finally {
                setLoading(false);
            }
        };

        bootstrap();
    }, []);

    const login = async (email, password) => {
        try {
            const res = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
                data = await res.json();
            } else {
                throw new Error(
                    res.status >= 500 || res.status === 404
                        ? 'El servidor no responde. Intente más tarde o contacte al administrador.'
                        : (res.statusText || 'Error de conexión con el servidor.')
                );
            }

            if (!res.ok) throw new Error(data.error || 'Credenciales incorrectas. Por favor intente nuevamente.');

            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            setUser(data.user);
            return {
                success: true,
                user: data.user,
                requiresOpenCaja: data.requiresOpenCaja === true,
                availableCajas: data.availableCajas || [],
                openCajaSession: data.openCajaSession || null
            };
        } catch (err) {
            console.error("Login failed:", err);
            return { success: false, error: err.message };
        }
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
